import {
  Annotation,
  AnnotationCommon,
  AnnotationRect,
  CircleAnnotation,
  EnhancedAnnotation,
  HtmlAnnotation,
  ImageAnnotation,
  MediaAnnotation,
  OverlayAnnotation,
  PathAnnotation,
  RectangleAnnotation,
} from '@readcloud/data';

import { ObjectId } from 'bson';

class AnnotationContainer<T extends Annotation> {
  config: {
    requiresCreationForm: boolean;
    minHeight: number;
    minWidth: number;
  };
  type: T['type'];

  constructor(
    type: T['type'],
    params: {
      requiresCreationForm: boolean;
      minHeight: number;
      minWidth: number;
    }
  ) {
    this.config = params;
    this.type = type;
  }

  public create = (params: {
    author: string;
    book: string;
    color: string;
    location: T['location'];
    data: T['data'];
    version?: T['version'];
    selectedText?: string;
    tags?: string[];
    options?: AnnotationCommon['options'];
  }) => ({
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
    clientCreated: new Date().toISOString(),
    clientUpdated: new Date().toISOString(),
    id: new ObjectId().valueOf().toString(),
    visibleInClouds: [],
    visibleToUsers: [],
    deleted: false,
    type: this.type,
    options: params.options,
    author: params.author,
    book: params.book,
    tags: params.tags || [],
    data: params.data,
    color: params.color,
    location: params.location,
    version: params.version || '3',
    selectedText: params.selectedText || '',
    bookType: 'PDF' as const,
  });
}

export const annotationContainers = {
  Path: new AnnotationContainer<PathAnnotation>('Path', {
    requiresCreationForm: false,
    minHeight: 50,
    minWidth: 50,
  }),
  Circle: new AnnotationContainer<CircleAnnotation>('Circle', {
    requiresCreationForm: false,
    minHeight: 50,
    minWidth: 50,
  }),
  OverlayText: new AnnotationContainer<OverlayAnnotation>('OverlayText', {
    requiresCreationForm: false,
    minHeight: 30,
    minWidth: 100,
  }),
  Rectangle: new AnnotationContainer<RectangleAnnotation>('Rectangle', {
    requiresCreationForm: false,
    minHeight: 10,
    minWidth: 10,
  }),
  HTML: new AnnotationContainer<HtmlAnnotation>('HTML', {
    requiresCreationForm: true,
    minHeight: 50,
    minWidth: 50,
  }),
  Image: new AnnotationContainer<ImageAnnotation>('Image', {
    requiresCreationForm: true,
    minHeight: 100,
    minWidth: 100,
  }),
  Media: new AnnotationContainer<MediaAnnotation>('Media', {
    requiresCreationForm: true,
    minHeight: 30,
    minWidth: 150,
  }),
} as const;

export const createEmptyAnnotation = ({
  author,
  book,
  color,
  pageNumber,
  rect,
  type,
  annoPrefs,
}: {
  author: string;
  book: string;
  color: string;
  pageNumber: number;
  rect: AnnotationRect;
  type: Annotation['type'];
  annoPrefs?: { [index: string]: Annotation['data'] };
}): EnhancedAnnotation => {
  const common = {
    location: {
      pageNumber,
    },
    author,
    book,
    color,
    version: '3' as const,
    selectedText: '',
  };

  const preferences = annoPrefs && annoPrefs[type];

  switch (type) {
    case 'Media':
      return annotationContainers[type].create({
        ...common,
        data: {
          variant: 'Embedded',
          ...preferences, // anything above this will get replaced if a preference exists for it.
          description: '',
          rect,
          url: '',
          scaleWithPage: true,
        },
      });

    case 'HTML':
      return annotationContainers[type].create({
        ...common,
        data: {
          variant: 'Embedded',
          ...preferences, // anything above this will get replaced if a preference exists for it.
          description: '',
          html: '',
          rect,
          scaleWithPage: true,
        },
      });
    case 'Image':
      return annotationContainers[type].create({
        ...common,
        data: {
          ...preferences, // anything above this will get replaced if a preference exists for it.
          description: '',
          rect,
          url: '',
          scaleWithPage: true,
        },
      });
    case 'Circle':
      return annotationContainers[type].create({
        ...common,
        data: {
          fill: color,
          stroke: '#00000000',
          strokeWidth: 0,
          ...preferences, // anything above this will get replaced if a preference exists for it.
          rect,
          scaleWithPage: true,
          belowContent: true,
        },
      });
    case 'Rectangle':
      return annotationContainers[type].create({
        ...common,
        data: {
          fill: color,
          stroke: '#00000000',
          strokeWidth: 0,
          ...preferences, // anything above this will get replaced if a preference exists for it.
          rect,
          scaleWithPage: true,
          belowContent: true,
        },
      });
    case 'OverlayText':
      return annotationContainers[type].create({
        ...common,
        data: {
          fill: '#000000',
          textAlign: 'left',
          fontFamily: 'Arial',
          fontSize: 16,
          fontWeight: 400,
          lineHeight: 1,
          overflowWrap: 'normal',
          wordBreak: 'normal',
          ...preferences,
          text: 'Enter Text',
          rect,
          scaleWithPage: true,
          belowContent: true,
        },
      });
    case 'Path':
      return annotationContainers[type].create({
        ...common,
        data: {
          strokeWidth: 4,
          stroke: '#000000',
          ...preferences,
          rect,
          paths: [],
          scaleWithPage: true,
          belowContent: true,
          description: '',
        },
      });
  }
};
